import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useAccesoConsumo } from './permisos';
import './office_panel.css';
import './consultas_ia.css';
import './consumo.css';

// Pantalla "Consumo" (desde el 25/10/2026). El titular la ve completa: usuarios, capacidad del servidor de Nawaiam,
// costo de los modelos de lenguaje y wallets. Quienes tienen el permiso de wallets ven solo las wallets.
const WALLET = `id clave nombre descripcion tipo saldoUsd consumoUsd agotada consultas30 ultimos30Usd mesUsd proyeccionMesUsd
  proyeccionMensualUsd estimadoUsd creadaAt cortesApi historicoUsd consultasHistoricas historicoEstimadoUsd historicoDesde
  cuentas { id empresaKey email desactivada }`;
const GET_RESUMEN = gql`
  query GetConsumoResumen {
    getConsumoResumen {
      usuarios { totales cuentasEmpresa usuariosChat personasPaneles personasDistintas personalOffice }
      capacidad { limite usadas cuentas personas libres porcentaje }
      modelos { modelo uso entrada cache5m lecturaCache salida }
      calibracion { charsPorToken promptFijo muestras fuente }
      porOrigen { origen nombre consultas usuarios costoUsd estimado costoPorConsultaUsd costoPorUsuarioUsd }
      costoPorUsuarioChatUsd ultimos30Usd mesUsd proyeccionMesUsd proyeccionMensualUsd estimadoUsd mes
      historicoUsd consultasHistoricas historicoEstimadoUsd historicoDesde
    }
  }
`;
const GET_WALLETS = gql`query GetWalletsConsumo { getWalletsConsumo { ${WALLET} } }`;
const GET_WALLET = gql`
  query GetWalletConsumo($id: ID!) {
    getWalletConsumo(id: $id) {
      wallet { ${WALLET} }
      movimientos { fecha tipo montoUsd saldoDespues nota por }
      consumoDiario { dia consultas costoUsd estimadoUsd }
    }
  }
`;
const GET_CUENTAS = gql`query GetCuentasParaWallet { getCuentasParaWallet { id empresaKey email walletId walletNombre } }`;
const GET_PERMISOS = gql`query GetPermisosWallets { getPermisosWallets { id email username role permisoWallets } }`;
const CREAR = gql`mutation CrearWallet($nombre: String!, $cuentas: [ID!]!, $tipo: String!, $saldoInicial: Float) {
  crearWallet(nombre: $nombre, cuentas: $cuentas, tipo: $tipo, saldoInicial: $saldoInicial) { id } }`;
const EDITAR = gql`mutation EditarWallet($id: ID!, $nombre: String, $cuentas: [ID!]) { editarWallet(id: $id, nombre: $nombre, cuentas: $cuentas) { id } }`;
const CARGAR = gql`mutation CargarSaldoWallet($id: ID!, $montoUsd: Float!, $nota: String) { cargarSaldoWallet(id: $id, montoUsd: $montoUsd, nota: $nota) { id saldoUsd } }`;
const TIPO = gql`mutation CambiarTipoWallet($id: ID!, $tipo: String!) { cambiarTipoWallet(id: $id, tipo: $tipo) { id tipo } }`;
const ELIMINAR = gql`mutation EliminarWallet($id: ID!) { eliminarWallet(id: $id) }`;
const PERMISO = gql`mutation SetPermisoWallets($userId: ID!, $habilitado: Boolean!) { setPermisoWallets(userId: $userId, habilitado: $habilitado) { id permisoWallets } }`;
const CAPACIDAD = gql`mutation SetCapacidadNawaiam($limite: Int!) { setCapacidadNawaiam(limite: $limite) }`;

const errorDe = (e) => (e && (e.graphQLErrors?.[0]?.message || e.message)) || 'No se pudo completar la operación.';
const num = (n) => Number(n || 0).toLocaleString('es-AR');
const usd = (n) => {
  const v = Number(n || 0);
  if (v === 0) return 'USD 0';
  const dec = Math.abs(v) < 1 ? 4 : 2;
  return `USD ${v.toLocaleString('es-AR', { minimumFractionDigits: dec, maximumFractionDigits: dec })}`;
};
const precio = (n) => `USD ${Number(n).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 3 })}`;
// "USD 249,41 · 3.536 preguntas": lo gastado y lo preguntado desde siempre; las preguntas, resaltadas en violeta
const preguntas = (n) => <span className="cs-preguntas">{num(n)} pregunta{n === 1 ? '' : 's'}</span>;
const acumulado = (w) => <>{usd(w.historicoUsd)} · {preguntas(w.consultasHistoricas)}</>;
const fecha = (d) => (d ? new Date(d).toLocaleDateString('es-AR') : '—');
const fechaHora = (d) => (d ? new Date(d).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' }) : '—');
const MOVIMIENTOS = { alta: 'Alta', carga: 'Carga de saldo', tipo: 'Cambio de tipo', edicion: 'Edición', baja: 'Baja' };
const REFRESCAR = ['GetWalletsConsumo', 'GetWalletConsumo', 'GetCuentasParaWallet'];

export default function Consumo() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { acceso, cargando } = useAccesoConsumo();
  const [params, setParams] = useSearchParams();
  const [flash, setFlash] = useState(null);
  const titular = acceso === 'titular';
  const pestanas = titular ? [['resumen', 'Resumen'], ['wallets', 'Wallets'], ['permisos', 'Permisos']] : [['wallets', 'Wallets']];
  const pestana = pestanas.some(([k]) => k === params.get('tab')) ? params.get('tab') : pestanas[0][0];
  const ir = (k) => setParams(k === pestanas[0][0] ? {} : { tab: k });
  const avisar = (texto, tipo = 'ok') => { setFlash({ texto, tipo }); setTimeout(() => setFlash(null), tipo === 'error' ? 8000 : 4000); };

  if (cargando) return <div className="page-container" style={{ maxWidth: 900 }}><div className="spinner" /></div>;
  if (!acceso) {
    return (
      <div className="page-container" style={{ maxWidth: 900 }}>
        <div className="panel-card" style={{ padding: 40, textAlign: 'center' }}>
          <h2 style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: 8 }}>Acceso denegado</h2>
          <p style={{ color: '#6b7280', marginBottom: 16 }}>Esta pantalla es solo para el titular de Consumo y quienes tienen su permiso.</p>
          <button className="btn-accent" onClick={() => navigate('/altaEmpresa')}>Volver</button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container cia cs" style={{ maxWidth: 1100 }}>
      <div className="office-header">
        <div className="office-header-left">
          <button className="btn-subtle" onClick={() => navigate('/altaEmpresa')}>← Volver</button>
          <h1 style={{ fontSize: '1.3rem', fontWeight: 700, letterSpacing: '-0.3px', margin: 0 }}>Consumo</h1>
          <span className="cs-subtitulo">{titular ? 'Usuarios, capacidad y costo de NAWI' : 'Wallets de consumo'}</span>
        </div>
        <button className="btn-subtle" onClick={() => logout()}>Cerrar sesión</button>
      </div>

      {pestanas.length > 1 && (
        <div className="cia-tabs" role="tablist">
          {pestanas.map(([k, t]) => (
            <button key={k} role="tab" aria-selected={pestana === k} className={`cia-tab ${pestana === k ? 'cia-tab--activa' : ''}`} onClick={() => ir(k)}>{t}</button>
          ))}
        </div>
      )}
      {flash && <div className={`cs-flash cs-flash--${flash.tipo}`} role="status">{flash.texto}</div>}

      {pestana === 'resumen' && <Resumen avisar={avisar} />}
      {pestana === 'wallets' && <Wallets titular={titular} avisar={avisar} />}
      {pestana === 'permisos' && <Permisos avisar={avisar} />}
    </div>
  );
}

// ── Resumen ────────────────────────────────────────────────────────────────────────────────────────────────────
function Resumen({ avisar }) {
  const { data, loading, error, refetch } = useQuery(GET_RESUMEN, { fetchPolicy: 'cache-and-network' });
  const [limite, setLimite] = useState('');
  const [guardarLimite, { loading: guardando }] = useMutation(CAPACIDAD);
  const r = data?.getConsumoResumen;
  if (error) return <div className="cs-aviso cs-aviso--error">{errorDe(error)}</div>;
  if (!r) return loading ? <div className="spinner" /> : null;
  const cap = r.capacidad;
  const barra = cap.porcentaje >= 90 ? 'lleno' : cap.porcentaje >= 75 ? 'alto' : 'ok';
  const chat = r.porOrigen.find((o) => o.origen === 'chat');
  const onLimite = async (e) => {
    e.preventDefault();
    try { await guardarLimite({ variables: { limite: parseInt(limite, 10) } }); setLimite(''); avisar('Capacidad actualizada.'); refetch(); } catch (x) { avisar(errorDe(x), 'error'); }
  };

  return (
    <>
      <div className="cia-kpis">
        <div className="panel-card cia-kpi cia-kpi--principal">
          <span className="cia-kpi-titulo">Usuarios totales</span>
          <span className="cia-kpi-valor">{num(r.usuarios.totales)}</span>
          <span className="cia-kpi-pie">Personas distintas por email entre cuentas, NAWI Chat, paneles y Office</span>
        </div>
        <div className="panel-card cia-kpi">
          <span className="cia-kpi-titulo">Servidor de Nawaiam</span>
          <span className="cia-kpi-valor">{num(cap.usadas)} <small>de {num(cap.limite)}</small></span>
          <div className={`cia-barra cia-barra--${barra}`} role="meter" aria-valuemin={0} aria-valuemax={cap.limite} aria-valuenow={cap.usadas} aria-label="Cuentas usadas del servidor de Nawaiam">
            <span style={{ width: `${Math.min(cap.porcentaje, 100)}%` }} />
          </div>
          <span className="cia-kpi-pie">{String(cap.porcentaje).replace('.', ',')}% usado · quedan {num(cap.libres)}</span>
        </div>
        <div className="panel-card cia-kpi">
          <span className="cia-kpi-titulo">Costo últimos 30 días</span>
          <span className="cia-kpi-valor">{usd(r.ultimos30Usd)}</span>
          <span className="cia-kpi-pie">{r.estimadoUsd > 0 ? `${usd(r.estimadoUsd)} estimado (NAWI Chat)` : 'Con tokens reales'}</span>
        </div>
        <div className="panel-card cia-kpi">
          <span className="cia-kpi-titulo">Proyección mensual</span>
          <span className="cia-kpi-valor">{usd(r.proyeccionMensualUsd)}</span>
          <span className="cia-kpi-pie">Al ritmo de los últimos 30 días</span>
        </div>
      </div>

      <div className="cs-dos">
        <section className="panel-card cia-seccion">
          <h2 className="cs-titulo">Usuarios</h2>
          <ul className="cs-lista">
            <li><span>Cuentas empresa activas</span><b>{num(r.usuarios.cuentasEmpresa)}</b></li>
            <li><span>Usuarios de NAWI Chat con acceso</span><b>{num(r.usuarios.usuariosChat)}</b></li>
            <li><span>Personas en paneles (cada aparición)</span><b>{num(r.usuarios.personasPaneles)}</b></li>
            <li><span>Personas en paneles (distintas)</span><b>{num(r.usuarios.personasDistintas)}</b></li>
            <li><span>Personal de Office con acceso</span><b>{num(r.usuarios.personalOffice)}</b></li>
          </ul>
        </section>
        <section className="panel-card cia-seccion">
          <h2 className="cs-titulo">Capacidad del servidor de Nawaiam</h2>
          <ul className="cs-lista">
            <li><span>Cuentas empresa activas</span><b>{num(cap.cuentas)}</b></li>
            <li><span>Personas en paneles (si está en dos paneles, cuenta dos)</span><b>{num(cap.personas)}</b></li>
            <li className="cs-lista-total"><span>Usadas</span><b>{num(cap.usadas)} de {num(cap.limite)}</b></li>
          </ul>
          <form className="cs-fila-form" onSubmit={onLimite}>
            <label className="cia-campo">Límite del servidor
              <input type="number" min="1" step="1" placeholder={String(cap.limite)} value={limite} onChange={(e) => setLimite(e.target.value)} style={{ width: 110 }} />
            </label>
            <button className="btn-outline" type="submit" disabled={!limite || guardando}>Guardar</button>
          </form>
        </section>
      </div>

      <section className="panel-card cia-seccion">
        <h2 className="cs-titulo">Costo de los modelos de lenguaje</h2>
        <div className="cs-totales">
          <div><span>Desde el origen ({fecha(r.historicoDesde)})</span><b>{usd(r.historicoUsd)}</b>{preguntas(r.consultasHistoricas)}</div>
          <div><span>Mes en curso ({r.mes})</span><b>{usd(r.mesUsd)}</b></div>
          <div><span>Proyección del mes</span><b>{usd(r.proyeccionMesUsd)}</b></div>
          <div><span>Costo promedio por usuario de NAWI Chat (30 días)</span><b>{usd(r.costoPorUsuarioChatUsd)}</b></div>
        </div>
        <div className="cia-tabla-scroll">
          <table className="cia-tabla cs-tabla">
            <thead><tr><th>Origen</th><th className="num">Consultas (30 días)</th><th className="num">Usuarios que consultaron</th><th className="num">Costo</th><th className="num">Por consulta</th><th className="num">Por usuario</th></tr></thead>
            <tbody>
              {r.porOrigen.map((o) => (
                <tr key={o.origen}>
                  <td>{o.nombre}{o.estimado && <span className="cia-estimado" title="Estimado: NAWI Chat no guarda los tokens que gasta">≈</span>}</td>
                  <td className="num">{num(o.consultas)}</td>
                  <td className="num">{num(o.usuarios)}</td>
                  <td className="num">{usd(o.costoUsd)}</td>
                  <td className="num">{usd(o.costoPorConsultaUsd)}</td>
                  <td className="num">{usd(o.costoPorUsuarioUsd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="panel-card cia-seccion">
        <h2 className="cs-titulo">Modelos activos</h2>
        <div className="cia-tabla-scroll">
          <table className="cia-tabla cs-tabla">
            <thead><tr><th>Modelo</th><th>Para qué se usa</th><th className="num">Entrada</th><th className="num">Escritura en caché</th><th className="num">Lectura de caché</th><th className="num">Salida</th></tr></thead>
            <tbody>
              {r.modelos.map((m) => (
                <tr key={m.modelo}>
                  <td><code>{m.modelo}</code></td><td className="cs-envolver">{m.uso}</td>
                  <td className="num">{precio(m.entrada)}</td><td className="num">{precio(m.cache5m)}</td><td className="num">{precio(m.lecturaCache)}</td><td className="num">{precio(m.salida)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="cia-nota" style={{ marginTop: 8 }}>Por millón de tokens, precios de Anthropic. La escritura en caché es la de 5 minutos, la que usan NAWI Chat y la API.</p>
      </section>

      <p className="cia-pie">
        ≈ NAWI Chat no guarda los tokens que gasta: su costo se estima con cada respuesta guardada (largo de la pregunta y de la respuesta,
        historial y si el prompt fijo seguía en caché), más la moderación y el resumen que hace con {r.modelos[1]?.modelo}.
        Calibración {r.calibracion.fuente === 'api' ? `con ${num(r.calibracion.muestras)} consultas reales de la API` : 'con valores por defecto (todavía no hay consultas reales en la API)'}:
        {' '}{String(r.calibracion.charsPorToken).replace('.', ',')} caracteres por token y {num(r.calibracion.promptFijo)} tokens de prompt fijo.
        {chat && chat.consultas === 0 ? ' En los últimos 30 días NAWI Chat no tuvo respuestas.' : ''} La API registra tokens reales.
      </p>
    </>
  );
}

// ── Wallets ────────────────────────────────────────────────────────────────────────────────────────────────────
function Wallets({ titular, avisar }) {
  const { data, loading, error } = useQuery(GET_WALLETS, { fetchPolicy: 'cache-and-network' });
  const [elegida, setElegida] = useState(null);
  const [creando, setCreando] = useState(false);
  const [buscar, setBuscar] = useState('');
  const [tipo, setTipo] = useState('todas');
  const lista = useMemo(() => data?.getWalletsConsumo || [], [data]);
  // Busca por nombre de la wallet o por el nombre o email de sus cuentas; nawi-1 siempre queda primera
  const visibles = useMemo(() => {
    const t = buscar.trim().toLowerCase();
    return lista.filter((w) => {
      if (tipo === 'prepaga' && w.tipo !== 'prepaga') return false;
      if (tipo === 'ilimitada' && w.tipo !== 'ilimitada') return false;
      if (tipo === 'sinsaldo' && !w.agotada) return false;
      if (!t) return true;
      return w.nombre.toLowerCase().includes(t) || w.cuentas.some((c) => (c.empresaKey || '').toLowerCase().includes(t) || (c.email || '').toLowerCase().includes(t));
    });
  }, [lista, buscar, tipo]);
  const cuantas = (k) => lista.filter((w) => (k === 'todas' ? true : k === 'sinsaldo' ? w.agotada : w.tipo === k)).length;
  if (elegida) return <DetalleWallet id={elegida} titular={titular} avisar={avisar} volver={() => setElegida(null)} />;
  if (creando) return <FormWallet avisar={avisar} listo={() => setCreando(false)} />;

  return (
    <>
      <div className="cs-herramientas">
        <div className="cs-buscar">
          <svg aria-hidden="true" viewBox="0 0 20 20" width="15" height="15"><circle cx="9" cy="9" r="6" fill="none" stroke="currentColor" strokeWidth="1.8" /><path d="M13.5 13.5 17 17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
          <input aria-label="Buscar wallets" placeholder="Buscar wallet, cuenta o email…" value={buscar} onChange={(e) => setBuscar(e.target.value)} />
        </div>
        <div className="cs-segmento" role="radiogroup" aria-label="Tipo de wallet">
          {[['todas', 'Todas'], ['prepaga', 'Prepagas'], ['ilimitada', 'Ilimitadas'], ['sinsaldo', 'Sin saldo']].map(([k, t]) => (
            <button key={k} type="button" role="radio" aria-checked={tipo === k} className={`cs-segmento-op ${tipo === k ? 'cs-segmento-op--activo' : ''}`} onClick={() => setTipo(k)}>
              {t}<span>{num(cuantas(k))}</span>
            </button>
          ))}
        </div>
        {titular && <button className="cs-boton-nuevo" onClick={() => setCreando(true)}>+ Nueva wallet</button>}
      </div>

      <details className="cs-ayuda-wallets">
        <summary>¿Cómo funcionan las wallets?</summary>
        <p>
          <b>nawi-1</b> junta todo el consumo del servidor (NAWI Chat y la API). Las wallets de empresa juntan el de sus cuentas.
          El <b>costo total</b> suma todas las charlas de NAWI Chat de sus cuentas desde que existen y la API desde que Office la registra.
          Las <b>prepagas</b> descuentan saldo con el consumo desde que se crean; las <b>ilimitadas</b> solo lo acumulan.
          Cuando una prepaga se queda sin saldo, la API deja de responder a sus cuentas y les pide cargar saldo (en un minuto como máximo);
          vuelve apenas cargás o la pasás a ilimitada. NAWI Chat todavía no corta: llega en la próxima fase.
        </p>
      </details>

      {error && <div className="cs-aviso cs-aviso--error">{errorDe(error)}</div>}
      {loading && !data && <div className="spinner" />}
      {data && visibles.length === 0 && <div className="cs-vacio">No hay wallets {buscar.trim() ? `que coincidan con "${buscar.trim()}"` : 'de este tipo'}.</div>}
      <div className="cs-wallets">
        {visibles.map((w) => (
          <button key={w.id} className={`cs-wallet ${w.clave ? 'cs-wallet--servidor' : ''} ${w.agotada ? 'cs-wallet--agotada' : ''}`} onClick={() => setElegida(w.id)}>
            <div className="cs-wallet-cabeza">
              <span className="cs-wallet-nombre" title={w.nombre}>{w.nombre}</span>
              <span className="cs-etiquetas">
                {w.clave && <span className="tag tag--purple">Servidor</span>}
                <span className={`tag ${w.tipo === 'prepaga' ? 'tag--yellow' : 'tag--blue'}`}>{w.tipo === 'prepaga' ? 'Prepaga' : 'Ilimitada'}</span>
                {w.agotada && <span className="tag tag--red">Sin saldo</span>}
              </span>
            </div>
            <div className="cs-wallet-monto">
              <span className="cs-wallet-rotulo">{w.tipo === 'prepaga' ? 'Saldo' : 'Costo total'}</span>
              <b className={w.tipo === 'prepaga' && w.saldoUsd <= 0 ? 'cs-negativo' : ''}>{usd(w.tipo === 'prepaga' ? w.saldoUsd : w.historicoUsd)}</b>
              <span className="cs-wallet-acum">
                {w.tipo === 'prepaga' && <>{usd(w.historicoUsd)} desde siempre · </>}
                {preguntas(w.consultasHistoricas)}{w.historicoDesde ? <span className="cs-tenue"> desde {fecha(w.historicoDesde)}</span> : null}
              </span>
            </div>
            <div className="cs-wallet-mini">
              <div><span>Este mes</span><b>{usd(w.mesUsd)}</b></div>
              <div><span>Proyección</span><b>{usd(w.proyeccionMesUsd)}</b></div>
              <div><span>30 días</span><b>{num(w.consultas30)}</b></div>
            </div>
            <div className="cs-wallet-pie">
              <span>{w.clave ? 'Todo el servidor' : `${w.cuentas.length} cuenta${w.cuentas.length === 1 ? '' : 's'}`}</span>
              {w.tipo === 'prepaga' && <span>Descontado: {usd(w.consumoUsd)}</span>}
              {w.cortesApi > 0 && <span className="cs-corte">API cortada · {w.cortesApi}</span>}
            </div>
          </button>
        ))}
      </div>
    </>
  );
}

function DetalleWallet({ id, titular, avisar, volver }) {
  const { data, loading, error } = useQuery(GET_WALLET, { variables: { id }, fetchPolicy: 'cache-and-network' });
  const [editando, setEditando] = useState(false);
  const [monto, setMonto] = useState('');
  const [nota, setNota] = useState('');
  const [confirmar, setConfirmar] = useState(null);   // 'tipo' | 'eliminar'
  const [cargar, { loading: cargando }] = useMutation(CARGAR, { refetchQueries: REFRESCAR });
  const [cambiarTipo, { loading: cambiando }] = useMutation(TIPO, { refetchQueries: REFRESCAR });
  const [eliminar, { loading: eliminando }] = useMutation(ELIMINAR, { refetchQueries: REFRESCAR });
  const d = data?.getWalletConsumo;
  if (error) return <><button className="btn-subtle" onClick={volver}>← Wallets</button><div className="cs-aviso cs-aviso--error" style={{ marginTop: 12 }}>{errorDe(error)}</div></>;
  if (!d) return loading ? <div className="spinner" /> : null;
  const w = d.wallet;
  if (editando) return <FormWallet inicial={w} avisar={avisar} listo={() => setEditando(false)} />;
  const otroTipo = w.tipo === 'prepaga' ? 'ilimitada' : 'prepaga';

  const onCargar = async (e) => {
    e.preventDefault();
    try { await cargar({ variables: { id, montoUsd: parseFloat(String(monto).replace(',', '.')), nota: nota || null } }); setMonto(''); setNota(''); avisar('Saldo cargado.'); } catch (x) { avisar(errorDe(x), 'error'); }
  };
  const onTipo = async () => {
    try { await cambiarTipo({ variables: { id, tipo: otroTipo } }); setConfirmar(null); avisar(`La wallet ahora es ${otroTipo}.`); } catch (x) { avisar(errorDe(x), 'error'); }
  };
  const onEliminar = async () => {
    try { await eliminar({ variables: { id } }); avisar('Wallet eliminada.'); volver(); } catch (x) { avisar(errorDe(x), 'error'); }
  };

  return (
    <>
      <button className="btn-subtle" onClick={volver}>← Wallets</button>
      <div className="panel-card cia-seccion cs-detalle-cabeza">
        <div>
          <h2 className="cs-detalle-nombre">{w.nombre}</h2>
          <span className="cia-nota">{w.descripcion || `${w.cuentas.length} cuenta(s) empresa`}{w.creadaAt ? ` · creada el ${fechaHora(w.creadaAt)}` : ''}</span>
        </div>
        <span className="cs-etiquetas">
          <span className={`tag ${w.tipo === 'prepaga' ? 'tag--yellow' : 'tag--blue'}`}>{w.tipo === 'prepaga' ? 'Prepaga: descuenta saldo' : 'Ilimitada: solo acumula'}</span>
          {w.agotada && <span className="tag tag--red">Sin saldo</span>}
        </span>
      </div>
      {w.agotada && (
        <div className="cs-aviso cs-aviso--error">
          Sin saldo: {w.cortesApi > 0 ? `la API está cortada para ${w.cortesApi} usuario${w.cortesApi === 1 ? '' : 's'} de esta wallet` : 'la API corta a sus usuarios en la próxima vuelta del cobrador (un minuto)'} y les responde
          "Tu cuenta no tiene saldo disponible para hacer consultas. Pedí al área comercial que cargue saldo." Cargá saldo o pasala a ilimitada para que vuelva.
        </div>
      )}

      <div className="cia-kpis">
        <div className="panel-card cia-kpi cia-kpi--principal">
          <span className="cia-kpi-titulo">{w.tipo === 'prepaga' ? 'Saldo' : 'Costo total'}</span>
          <span className={`cia-kpi-valor ${w.tipo === 'prepaga' && w.saldoUsd <= 0 ? 'cs-negativo' : ''}`}>{usd(w.tipo === 'prepaga' ? w.saldoUsd : w.historicoUsd)}</span>
          <span className="cia-kpi-pie">
            Acumulado desde siempre{w.historicoDesde ? ` (desde ${fecha(w.historicoDesde)})` : ''}: <b>{acumulado(w)}</b>
            {w.tipo === 'prepaga' && <><br />Descontado desde que existe: {usd(w.consumoUsd)}</>}
            {w.historicoEstimadoUsd > 0 && <><br />{usd(w.historicoEstimadoUsd)} estimado (NAWI Chat)</>}
          </span>
        </div>
        <div className="panel-card cia-kpi"><span className="cia-kpi-titulo">Este mes</span><span className="cia-kpi-valor">{usd(w.mesUsd)}</span><span className="cia-kpi-pie">Proyección: {usd(w.proyeccionMesUsd)}</span></div>
        <div className="panel-card cia-kpi"><span className="cia-kpi-titulo">Últimos 30 días</span><span className="cia-kpi-valor">{usd(w.ultimos30Usd)}</span><span className="cia-kpi-pie">{num(w.consultas30)} consultas{w.estimadoUsd > 0 ? ` · ${usd(w.estimadoUsd)} estimado` : ''}</span></div>
        <div className="panel-card cia-kpi"><span className="cia-kpi-titulo">Proyección mensual</span><span className="cia-kpi-valor">{usd(w.proyeccionMensualUsd)}</span><span className="cia-kpi-pie">Al ritmo de los últimos 30 días</span></div>
      </div>

      {titular && (
        <section className="panel-card cia-seccion">
          <h3 className="cs-titulo">Acciones</h3>
          <form className="cs-fila-form" onSubmit={onCargar}>
            <label className="cia-campo">Cargar saldo (USD)
              <input inputMode="decimal" placeholder="100" value={monto} onChange={(e) => setMonto(e.target.value)} style={{ width: 100 }} />
            </label>
            <label className="cia-campo">Nota
              <input placeholder="Opcional" value={nota} onChange={(e) => setNota(e.target.value)} maxLength={200} style={{ width: 200 }} />
            </label>
            <button className="btn-accent" type="submit" disabled={!monto || cargando}>{cargando ? 'Cargando…' : 'Cargar'}</button>
          </form>
          {w.tipo === 'ilimitada' && <p className="cia-nota">El saldo se guarda aunque la wallet sea ilimitada: se empieza a descontar cuando la pases a prepaga.</p>}
          <div className="cs-acciones cs-acciones--fila">
            <button className="btn-outline" onClick={() => setConfirmar('tipo')}>Pasar a {otroTipo}</button>
            {!w.clave && <button className="btn-outline" onClick={() => setEditando(true)}>Editar nombre y cuentas</button>}
            {!w.clave && <button className="btn-outline cs-peligro" onClick={() => setConfirmar('eliminar')}>Eliminar wallet</button>}
          </div>
          {confirmar === 'tipo' && (
            <div className="cs-confirmar">
              <p>{otroTipo === 'prepaga'
                ? `Desde ahora el consumo descuenta saldo. Saldo actual: ${usd(w.saldoUsd)}${w.saldoUsd <= 0 ? ' (queda sin saldo hasta que cargues)' : ''}.`
                : 'Desde ahora el consumo solo se acumula: no descuenta saldo ni la deja sin saldo. El saldo que tenga se conserva.'}</p>
              <div className="cs-acciones cs-acciones--fila">
                <button className="btn-outline" onClick={() => setConfirmar(null)}>Cancelar</button>
                <button className="btn-accent" onClick={onTipo} disabled={cambiando}>Pasar a {otroTipo}</button>
              </div>
            </div>
          )}
          {confirmar === 'eliminar' && (
            <div className="cs-confirmar cs-confirmar--peligro">
              <p>Se elimina la wallet y sus cuentas quedan libres para otra. El consumo ya registrado y sus movimientos se conservan en la base.</p>
              <div className="cs-acciones cs-acciones--fila">
                <button className="btn-outline" onClick={() => setConfirmar(null)}>Cancelar</button>
                <button className="btn-accent cs-boton-peligro" onClick={onEliminar} disabled={eliminando}>Eliminar wallet</button>
              </div>
            </div>
          )}
        </section>
      )}

      {!w.clave && (
        <section className="panel-card cia-seccion">
          <h3 className="cs-titulo">Cuentas empresa</h3>
          <ul className="cs-lista">
            {w.cuentas.map((c) => <li key={c.id}><span>{c.empresaKey}{c.desactivada ? ' (desactivada)' : ''}</span><b className="cs-email">{c.email}</b></li>)}
          </ul>
        </section>
      )}

      <div className="cs-dos">
        <section className="panel-card cia-seccion">
          <h3 className="cs-titulo">Consumo por día</h3>
          {d.consumoDiario.length === 0 ? <p className="cia-nota">Todavía no hay consumo registrado desde que existe la wallet.</p> : (
            <div className="cia-tabla-scroll">
              <table className="cia-tabla cs-tabla">
                <thead><tr><th>Día</th><th className="num">Consultas</th><th className="num">Costo</th></tr></thead>
                <tbody>{d.consumoDiario.map((x) => (
                  <tr key={x.dia}><td>{x.dia.split('-').reverse().join('/')}</td><td className="num">{num(x.consultas)}</td>
                    <td className="num">{usd(x.costoUsd)}{x.estimadoUsd > 0 && <span className="cia-estimado" title={`${usd(x.estimadoUsd)} estimado (NAWI Chat)`}>≈</span>}</td></tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </section>
        <section className="panel-card cia-seccion">
          <h3 className="cs-titulo">Movimientos</h3>
          <div className="cia-tabla-scroll">
            <table className="cia-tabla cs-tabla">
              <thead><tr><th>Fecha</th><th>Qué</th><th className="num">Monto</th><th className="num">Saldo</th></tr></thead>
              <tbody>{d.movimientos.map((m, i) => (
                <tr key={i} title={[m.nota, m.por].filter(Boolean).join(' · ')}>
                  <td>{fechaHora(m.fecha)}</td>
                  <td className="cs-envolver">{MOVIMIENTOS[m.tipo] || m.tipo}{m.nota ? <span className="cia-nota"> · {m.nota}</span> : null}</td>
                  <td className="num">{m.montoUsd != null ? usd(m.montoUsd) : '—'}</td>
                  <td className="num">{m.saldoDespues != null ? usd(m.saldoDespues) : '—'}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </section>
      </div>
    </>
  );
}

function FormWallet({ inicial, avisar, listo }) {
  const { data, loading } = useQuery(GET_CUENTAS, { fetchPolicy: 'network-only' });
  const [nombre, setNombre] = useState(inicial ? inicial.nombre : '');
  const [tipo, setTipo] = useState('prepaga');
  const [saldo, setSaldo] = useState('');
  const [elegidas, setElegidas] = useState(() => new Set(inicial ? inicial.cuentas.map((c) => c.id) : []));
  const [buscar, setBuscar] = useState('');
  const [crear, { loading: creando }] = useMutation(CREAR, { refetchQueries: REFRESCAR });
  const [editar, { loading: editando }] = useMutation(EDITAR, { refetchQueries: REFRESCAR });
  const cuentas = useMemo(() => {
    const t = buscar.trim().toLowerCase();
    return (data?.getCuentasParaWallet || []).filter((c) => !t || (c.empresaKey || '').toLowerCase().includes(t) || (c.email || '').toLowerCase().includes(t));
  }, [data, buscar]);
  const alternar = (id) => setElegidas((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const onGuardar = async (e) => {
    e.preventDefault();
    try {
      if (inicial) await editar({ variables: { id: inicial.id, nombre, cuentas: [...elegidas] } });
      else await crear({ variables: { nombre, cuentas: [...elegidas], tipo, saldoInicial: tipo === 'prepaga' && saldo ? parseFloat(String(saldo).replace(',', '.')) : null } });
      avisar(inicial ? 'Wallet actualizada.' : 'Wallet creada.');
      listo();
    } catch (x) { avisar(errorDe(x), 'error'); }
  };

  return (
    <form className="panel-card cia-seccion" onSubmit={onGuardar}>
      <h2 className="cs-titulo">{inicial ? `Editar "${inicial.nombre}"` : 'Nueva wallet'}</h2>
      <span className="manual-label">Nombre descriptivo</span>
      <input className="manual-input" placeholder="Ej: CFOTECH SRL" value={nombre} onChange={(e) => setNombre(e.target.value)} maxLength={80} />
      {!inicial && (
        <>
          <span className="manual-label">Tipo</span>
          <div className="cs-tipos" role="radiogroup">
            <label className={`cs-tipo ${tipo === 'prepaga' ? 'cs-tipo--elegido' : ''}`}><input type="radio" name="tipo" checked={tipo === 'prepaga'} onChange={() => setTipo('prepaga')} />
              <span><b>Prepaga</b><br />El consumo descuenta saldo. Sin saldo, la API les pide cargar saldo a sus cuentas (NAWI Chat, en la próxima fase).</span></label>
            <label className={`cs-tipo ${tipo === 'ilimitada' ? 'cs-tipo--elegido' : ''}`}><input type="radio" name="tipo" checked={tipo === 'ilimitada'} onChange={() => setTipo('ilimitada')} />
              <span><b>Ilimitada</b><br />Solo acumula el costo, sin saldo ni límite.</span></label>
          </div>
          {tipo === 'prepaga' && (
            <>
              <span className="manual-label">Saldo inicial (USD)</span>
              <input className="manual-input" inputMode="decimal" placeholder="Ej: 100 (podés cargarlo después)" value={saldo} onChange={(e) => setSaldo(e.target.value)} />
            </>
          )}
        </>
      )}
      <span className="manual-label">Cuentas empresa ({elegidas.size} elegida{elegidas.size === 1 ? '' : 's'})</span>
      <input className="search-bar" placeholder="Buscar por nombre o email…" value={buscar} onChange={(e) => setBuscar(e.target.value)} style={{ marginBottom: 8 }} />
      {loading && !data ? <div className="spinner" /> : (
        <div className="cs-cuentas">
          {cuentas.map((c) => {
            const enOtra = c.walletId && (!inicial || c.walletId !== inicial.id);
            return (
              <label key={c.id} className={`cs-cuenta ${enOtra ? 'cs-cuenta--ocupada' : ''}`}>
                <input type="checkbox" checked={elegidas.has(c.id)} disabled={!!enOtra} onChange={() => alternar(c.id)} />
                <span className="cs-cuenta-nombre">{c.empresaKey}</span>
                <span className="cs-email">{c.email}</span>
                {enOtra && <span className="tag tag--gray">en {c.walletNombre}</span>}
              </label>
            );
          })}
          {cuentas.length === 0 && <p className="cia-nota">No hay cuentas para esta búsqueda.</p>}
        </div>
      )}
      <div className="cs-acciones cs-acciones--fila" style={{ marginTop: 14 }}>
        <button type="button" className="btn-outline" onClick={listo}>Cancelar</button>
        <button type="submit" className="btn-accent" disabled={creando || editando || !nombre.trim() || elegidas.size === 0}>{inicial ? 'Guardar' : 'Crear wallet'}</button>
      </div>
    </form>
  );
}

// ── Permisos ───────────────────────────────────────────────────────────────────────────────────────────────────
function Permisos({ avisar }) {
  const { data, loading, error } = useQuery(GET_PERMISOS, { fetchPolicy: 'cache-and-network' });
  const [cambiar] = useMutation(PERMISO);
  const onCambiar = async (p) => {
    try { await cambiar({ variables: { userId: p.id, habilitado: !p.permisoWallets } }); avisar(p.permisoWallets ? `${p.email} ya no ve las wallets.` : `${p.email} ahora ve las wallets.`); } catch (x) { avisar(errorDe(x), 'error'); }
  };
  if (error) return <div className="cs-aviso cs-aviso--error">{errorDe(error)}</div>;
  if (!data) return loading ? <div className="spinner" /> : null;
  return (
    <section className="panel-card cia-seccion">
      <h2 className="cs-titulo">Quién ve las wallets</h2>
      <p className="cia-nota" style={{ marginBottom: 10 }}>Con el permiso ven las wallets y su consumo, sin poder crearlas, cargar saldo ni dar permisos. El resto de esta pestaña es solo tuyo.</p>
      {data.getPermisosWallets.length === 0 ? <p className="cia-nota">No hay otro personal de Office con acceso.</p> : (
        <ul className="cs-lista">
          {data.getPermisosWallets.map((p) => (
            <li key={p.id}>
              <span><b className="cs-email">{p.email}</b> <span className="cia-nota">{p.role}</span></span>
              <button className={p.permisoWallets ? 'btn-outline' : 'btn-accent'} onClick={() => onCambiar(p)}>{p.permisoWallets ? 'Quitar permiso' : 'Dar permiso'}</button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
